import React, { useMemo, useState } from 'react';
import { Modal, Text, Area, Select, Banner, Check } from './ui';
import { useStore, uid } from '../state/store';
import {
  ACTION_PRIORITIES,
  MOVE_STATUSES,
  ACTION_CLOSED_STATUSES,
  ACTION_GATES,
  ACTION_LINK_KINDS,
  ACTION_RESULTS,
  EXPECTED_EVIDENCE_OPTIONS,
  MOVE_DISPOSITIONS,
} from '../lib/constants';
import { newAction, closeAsWaitingExternal, commercialCollectionPath, evaluateManualMove } from '../lib/actions';
import { fireCrmWriteback } from '../state/api';
import { crmWritebackForCloseout } from '../lib/crm';
import { orgWorkstreams } from '../lib/orgPlan';
import { assertWithinSprintWindow, defaultOperationalDue } from '../lib/sprint';

function linkOptions(state, kind) {
  if (kind === 'account') {
    return (state.accounts || []).map((a) => ({ value: a.id, label: a.name || a.id }));
  }
  if (kind === 'opportunity') {
    return (state.opportunities || []).map((o) => ({
      value: o.id,
      label: o.name || o.accountName || o.id,
    }));
  }
  if (kind === 'asset') {
    return (state.assetCandidates || []).map((a) => ({ value: a.id, label: a.name || a.id }));
  }
  if (kind === 'partner') {
    return (state.d001?.partners || []).map((p) => ({ value: p.id, label: p.org || p.name || p.id }));
  }
  return [];
}

function labelFor(state, kind, id) {
  return linkOptions(state, kind).find((o) => o.value === id)?.label || '';
}

const EMPTY = {
  text: '',
  workstream: '',
  priority: 'High',
  expectedEvidence: 'E3',
  dueDate: '',
  owner: 'Founder',
  status: 'Ready',
  gateRelevance: 'none',
  linkedKind: '',
  linkedId: '',
  blocker: '',
  whyNow: '',
  completionCondition: '',
  selectedForToday: false,
  completionResult: '',
  evidenceCreated: '',
  nextActionText: '',
  outcomeNote: '',
};

export function ActionForm({ draft, onClose, onSaved }) {
  const { state, add, patch, today, d } = useStore();
  const workstreamOptions = orgWorkstreams(state).map((w) => w.label);
  const defaultWs = workstreamOptions[0] || '';
  const existing = draft && !draft.isNew ? draft : null;
  const locked = existing && ['Closed', 'Completed', 'Cancelled', 'Superseded'].includes(existing.status);
  const defaultDue = defaultOperationalDue(d?.sprint) || today;
  const [challenge, setChallenge] = useState(null);
  const [dateError, setDateError] = useState('');
  const [f, setF] = useState(() => {
    if (existing) {
      return {
        ...EMPTY,
        ...existing,
        dueDate: existing.dueDate || defaultDue,
      };
    }
    return {
      ...EMPTY,
      dueDate: defaultDue,
      selectedForToday: !!draft?.selectedForToday,
      text: draft?.text || '',
      expectedEvidence: draft?.expectedEvidence || 'E3',
      workstream: draft?.workstream || defaultWs,
      linkedKind: draft?.linkedKind || '',
      linkedId: draft?.linkedId || '',
    };
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const links = useMemo(() => linkOptions(state, f.linkedKind), [state, f.linkedKind]);
  const closing = ACTION_CLOSED_STATUSES.includes(f.status);
  const blocked = f.status === 'Blocked';
  const needsEvidence = (f.status === 'Completed' || f.status === 'Closed') && f.expectedEvidence !== 'OPS';
  const complete =
    String(f.text).trim().length > 8 &&
    !!String(f.dueDate || '').trim() &&
    (existing || !!String(f.whyNow || '').trim() || !!challenge) &&
    (!blocked || String(f.blocker).trim()) &&
    (!needsEvidence || !!f.completionResult);

  const persist = (payload) => {
    const windowErr = assertWithinSprintWindow(payload.dueDate, state, 'dueDate');
    if (windowErr) {
      setDateError(windowErr);
      return;
    }
    setDateError('');
    const commercialPath = commercialCollectionPath(f.linkedKind);
    const waitingClose = !!(existing && closing && f.disposition === 'commercial' && f.linkedId && commercialPath);
    if (existing) {
      if (waitingClose) {
        const result = closeAsWaitingExternal(
          { ...payload, linkedId: f.linkedId, linkedKind: f.linkedKind, linkedLabel: payload.linkedLabel },
          { waitingUntil: f.waitingUntil, waitingFor: f.waitingFor || f.nextActionText, today: defaultDue },
        );
        patch('top3', existing.id, { ...payload, ...result.moveChanges });
        patch(commercialPath, f.linkedId, result.commercialChanges);
        add('top3', { ...result.followUp, id: uid('t3') });
      } else {
        patch('top3', existing.id, payload);
        if ((f.status === 'Completed' || f.status === 'Closed') && String(f.nextActionText).trim()) {
          add(
            'top3',
            newAction(
              {
                id: uid('t3'),
                text: f.nextActionText.trim(),
                workstream: f.workstream,
                priority: f.priority,
                expectedEvidence: f.expectedEvidence,
                dueDate: defaultDue,
                owner: f.owner,
                gateRelevance: f.gateRelevance,
                linkedKind: f.linkedKind,
                linkedId: f.linkedId,
                linkedLabel: payload.linkedLabel,
                selectedForToday: false,
                status: 'Ready',
                source: 'Manual',
              },
              defaultDue,
            ),
          );
        }
      }
    } else {
      add('top3', { ...payload, id: uid('t3') });
    }
    onSaved?.();
    onClose();
    if (existing && (closing || waitingClose)) {
      fireCrmWriteback(crmWritebackForCloseout({
        ...payload,
        nextActionText: f.nextActionText,
        waitingUntil: f.waitingUntil,
        waitingFor: f.waitingFor,
      }));
    }
  };

  const submit = (force = false, park = false) => {
    const linkedLabel = f.linkedKind === 'blocker' ? f.blocker : labelFor(state, f.linkedKind, f.linkedId);
    if (!existing) {
      const verdict = evaluateManualMove(
        { ...f, linkedLabel, completionCondition: f.completionCondition || f.text },
        { exceptions: state.exceptions, state },
      );
      if (verdict.challenge && !force && !park) {
        setChallenge(verdict);
        return;
      }
    }
    const payload = newAction(
      {
        ...f,
        text: f.text.trim(),
        linkedLabel,
        whyNow: f.whyNow,
        completionCondition: f.completionCondition || f.text.trim(),
        outcome: f.completionResult,
        source: existing ? f.source || 'Manual' : 'Manual',
        selectedForToday: park ? false : (closing ? false : f.selectedForToday),
        status: park ? 'Closed' : f.status,
        disposition: park ? 'stopped' : f.disposition,
        completedAt: park || f.status === 'Completed' || f.status === 'Closed' ? f.completedAt || today : f.completedAt || '',
        closedAt: park || closing ? today : '',
        outcomeNote: park
          ? (f.outcomeNote || 'Parked — not linked to a current gate, blocker, or commercial object.')
          : f.outcomeNote,
      },
      today,
    );
    persist(payload);
  };

  return (
    <Modal
      title={existing ? (locked ? 'Move record' : 'Edit move') : 'Add move'}
      sub="Exception path. Link a commercial object, say why it matters, and name the evidence it should produce."
      onClose={onClose}
      wide
      footer={
        challenge ? (
          <>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn" onClick={() => submit(false, true)}>Park</button>
            <button className="btn primary" onClick={() => submit(true, false)}>Add anyway</button>
          </>
        ) : (
          <>
            <button className="btn" onClick={onClose}>{locked ? 'Close' : 'Cancel'}</button>
            {!locked && (
              <button className="btn primary" onClick={() => submit()} disabled={!complete}>
                {existing ? 'Save move' : 'Add move'}
              </button>
            )}
          </>
        )
      }
    >
      {challenge && (
        <Banner tone="stop">
          {challenge.message}
        </Banner>
      )}

      {dateError ? <Banner tone="stop">{dateError}</Banner> : null}

      {locked && (
        <Banner>
          Closed moves are the audit trail. They are not edited.
        </Banner>
      )}

      <Area
        label="Move"
        value={f.text}
        onChange={set('text')}
        rows={2}
        placeholder="The specific commercial move. Named counterparty if there is one."
      />

      <Area
        label="Why it matters"
        value={f.whyNow}
        onChange={set('whyNow')}
        rows={2}
        placeholder="Why this is the highest-leverage move now — gate, blocker, or commercial object."
      />

      <Text
        label="Expected outcome"
        value={f.completionCondition}
        onChange={set('completionCondition')}
        placeholder="What will be true when this move is done."
      />

      <div className="grid g3">
        <Select label="Workstream" value={f.workstream} onChange={set('workstream')} options={workstreamOptions} />
        <Select label="Priority" value={f.priority} onChange={set('priority')} options={ACTION_PRIORITIES} />
        <Select
          label="Expected evidence"
          value={f.expectedEvidence}
          onChange={set('expectedEvidence')}
          options={EXPECTED_EVIDENCE_OPTIONS}
        />
      </div>

      <div className="grid g3">
        <Text label="Due date" type="date" value={f.dueDate} onChange={set('dueDate')} />
        <Text label="Owner" value={f.owner} onChange={set('owner')} />
        <Select
          label="Gate relevance"
          value={f.gateRelevance}
          onChange={set('gateRelevance')}
          options={ACTION_GATES}
        />
      </div>

      <div className="grid g3">
        <Select label="Status" value={f.status} onChange={set('status')} options={[...MOVE_STATUSES, 'Blocked']} />
        <Select
          label="Linked to"
          value={f.linkedKind}
          onChange={(v) => setF((x) => ({ ...x, linkedKind: v, linkedId: '' }))}
          options={ACTION_LINK_KINDS}
        />
        {f.linkedKind && f.linkedKind !== 'blocker' ? (
          <Select
            label={ACTION_LINK_KINDS.find((k) => k.value === f.linkedKind)?.label || 'Record'}
            value={f.linkedId}
            onChange={set('linkedId')}
            options={[{ value: '', label: 'Select…' }, ...links]}
          />
        ) : (
          <div />
        )}
      </div>

      {(blocked || f.linkedKind === 'blocker') && (
        <Area
          label="Blocker"
          value={f.blocker}
          onChange={set('blocker')}
          rows={2}
          placeholder="Name the permission, counterparty, missing document, or decision that is blocking this."
          help={blocked && !String(f.blocker).trim() ? 'A blocked action must name the blocker.' : undefined}
        />
      )}

      {(f.status === 'Completed' || f.status === 'Closed') && (
        <>
          <Select
            label="Disposition"
            value={f.disposition || ''}
            onChange={set('disposition')}
            options={[{ value: '', label: 'Choose…' }, ...MOVE_DISPOSITIONS]}
          />
          <div className="grid g2">
            <Select
              label="Completion result"
              value={f.completionResult}
              onChange={set('completionResult')}
              options={ACTION_RESULTS}
              help="Did this action create the evidence it named?"
            />
            <Select
              label="Evidence actually created"
              value={f.evidenceCreated}
              onChange={set('evidenceCreated')}
              options={[{ value: '', label: 'None' }, { value: 'ACT', label: 'Activity only' }, ...EXPECTED_EVIDENCE_OPTIONS]}
            />
          </div>
          <Area
            label="Next action, if applicable"
            value={f.nextActionText}
            onChange={set('nextActionText')}
            rows={2}
            placeholder="The commercial follow-up this completion creates. Saved as a new open action."
          />
          {f.disposition === 'commercial' && f.linkedId && commercialCollectionPath(f.linkedKind) && (
            <div className="grid g2">
              <Text label="Waiting until" type="date" value={f.waitingUntil || ''} onChange={set('waitingUntil')} />
              <Text
                label="Waiting for"
                value={f.waitingFor || ''}
                onChange={set('waitingFor')}
                placeholder="Named reply, document, or decision"
              />
            </div>
          )}
        </>
      )}

      <Area label="Note" value={f.outcomeNote} onChange={set('outcomeNote')} rows={2} placeholder="Optional. What actually happened." />

      {!existing && (d.top3?.today || []).length < 3 && (
        <Check
          label="Commit to Today (exception)"
          checked={!!f.selectedForToday}
          onChange={set('selectedForToday')}
        />
      )}
    </Modal>
  );
}
